import { Injectable, LOCALE_ID, Inject } from '@angular/core';
import { formatDate } from '@angular/common';
import * as moment from 'moment-timezone';
import 'moment/locale/es';  // 🌍 Importar locale español para moment.js

/**
 * 🛩️ SERVICIO INTERNACIONAL DE FECHAS PARA MANTENIMIENTO AERONÁUTICO
 * -------------------------------------------------------------------
 * 
 * 📚 **GUÍA RÁPIDA PARA PROGRAMADORES: TIPOS DE FECHA Y SU USO EN AVIACIÓN**
 *
 * En el desarrollo de software aeronáutico, el manejo correcto de fechas y horas es CRÍTICO.
 * Aquí te explico los tipos de fecha más importantes, CUÁNDO y DÓNDE usarlos, y ejemplos prácticos.
 *
 * -------------------------------------------------------------------
 *
 * 1️⃣ **localDate** ("YYYY-MM-DD")
 *    - Qué es: Fecha local según la zona horaria configurada (ej: Colombia)
 *    - Uso: Mostrar fechas en la UI, inputs de usuario, reportes visuales
 *    - Ejemplo: "2025-07-01"
 *
 * 2️⃣ **localDateTime** ("YYYY-MM-DD HH:mm:ss")
 *    - Qué es: Fecha y hora local, útil para mostrar detalles precisos en la UI
 *    - Uso: Detalles de eventos, logs visuales, filtros por hora local
 *    - Ejemplo: "2025-07-01 20:10:41"
 * 
 * 3️⃣ **utcTimestamp** (ISO 8601 UTC, "YYYY-MM-DDTHH:mm:ss.sssZ")
 *    - Qué es: Fecha y hora universal (UTC/Zulu), estándar internacional
 *    - Uso: Enviar/recibir datos a APIs, logs técnicos, trazabilidad, cumplimiento FAA/ICAO
 *    - Ejemplo: "2025-07-01T01:10:41.825Z"
 *
 * 4️⃣ **zuluTime** ("YYYY-MM-DDTHH:mm:ssZ")
 *    - Qué es: Formato Zulu, requerido en registros aeronáuticos internacionales
 *    - Uso: Logs de mantenimiento, bitácoras, reportes oficiales
 *    - Ejemplo: "2025-07-01T01:10:41Z"
 *
 * 5️⃣ **unixTimestamp** (número)
 *    - Qué es: Segundos desde 1970-01-01T00:00:00Z (Epoch)
 *    - Uso: Cálculos, comparaciones, ordenamiento, bases de datos
 *    - Ejemplo: 1751418641
 * 
 * -------------------------------------------------------------------
 *
 * ⚠️ **CONFUSIÓN DE NOMENCLATURA EN mensajes.service.ts (CRÍTICO):**
 * 
 * 🚨 **IMPORTANTE**: El servicio anterior (mensajes.service.ts) tenía métodos con nombres CONFUSOS:
 * 
 * - `date_with_UTC(fecha, format)` - NAMING CONFUSO: ❌ NO usaba UTC real, solo conversión local
 * - `date_without_UTC(fecha, format)` - NAMING ERRÓNEO: ✅ SÍ usaba UTC con moment.utc()
 * 
 * ⚠️ **ESTO CAUSABA CONFUSIÓN**: Los nombres de los métodos eran OPUESTOS a su comportamiento real.
 * 
 * 🔧 **MIGRACIÓN CORRECTA**:
 * - Para fechas locales: usar `formatLocalized()` de este servicio
 * - Para fechas UTC: usar `formatWithUTC()` de este servicio
 * - Para timestamp aeronáutico: usar `generateAviationTimestamp()`
 * 
 * -------------------------------------------------------------------
 *
 * ⚠️ **Errores comunes a evitar:**
 * - Usar `new Date().toISOString().split('T')[0]` para fechas locales: puede dar el día incorrecto por timezone.
 * - No convertir a UTC antes de enviar a la API: puede causar desfases de horas/días.
 * - Mostrar UTC al usuario final: confunde, siempre mostrar localDate/localDateTime en la UI.
 * - Confundir métodos deprecated de mensajes.service.ts por sus nombres incorrectos.
 *
 * -------------------------------------------------------------------
 *
 * ✅ **Buenas prácticas:**
 * - Siempre usa los métodos de este servicio para obtener y convertir fechas.
 * - Para la UI: usa localDate/localDateTime.
 * - Para APIs/logs: usa utcTimestamp o zuluTime.
 * - Para cálculos: usa unixTimestamp.
 * - Para validaciones/comparaciones: usa los métodos isToday, isSameDay, etc.
 * - NO uses métodos deprecated de mensajes.service.ts (están marcados con warnings).
 *
 * -------------------------------------------------------------------
 *
 * 🔍 **Ejemplo práctico:**
 *
 *   const ts = this.dateService.generateAviationTimestamp();
 *   // UI: ts.localDate, ts.localDateTime
 *   // API: ts.utcTimestamp
 *   // Log: ts.zuluTime
 *   // Cálculo: ts.unixTimestamp
 *
 * -------------------------------------------------------------------
 *
 * Si tienes dudas, revisa la documentación o pregunta al líder técnico.
 *
 * -------------------------------------------------------------------
 */

export interface DateRange {
  startDay: string;
  endDay: string;
  firstDayOfMonth: string;
  lastDayOfMonth: string;
  today: string;
  todayFormatted: string;
}

export interface TimeZoneInfo {
  name: string;
  offset: string;
  abbreviation: string;
}

export interface AviationTimestamp {
  localDate: string;      // Para mostrar en UI (YYYY-MM-DD)
  localDateTime: string;  // Para mostrar en UI (YYYY-MM-DD HH:mm:ss)
  utcTimestamp: string;   // Para APIs y logs (ISO 8601 UTC)
  zuluTime: string;       // Para logs aeronáuticos (Zulu format)
  unixTimestamp: number;  // Para comparaciones y cálculos
}

export type DateFormat =
  | 'short' | 'medium' | 'long' | 'full'
  | 'shortDate' | 'mediumDate' | 'longDate' | 'fullDate'
  | 'shortTime' | 'mediumTime' | 'longTime' | 'fullTime'
  | string; // Custom format

@Injectable({
  providedIn: 'root'
})
export class InternationalDateService {

  // 🌐 Configuración de zonas horarias principales para la aplicación
  private readonly SUPPORTED_TIMEZONES = {
    'America/Bogota': 'COT', // Colombia
    'America/New_York': 'EST/EDT', // Estados Unidos - Este
    'America/Los_Angeles': 'PST/PDT', // Estados Unidos - Oeste
    'America/Mexico_City': 'CST/CDT', // México
    'America/Sao_Paulo': 'BRT', // Brasil
    'Europe/Madrid': 'CET/CEST', // España
    'UTC': 'UTC' // Tiempo universal (Zulu)
  };

  // 🎯 Zona horaria por defecto (fallback hardcoded antes del login)
  private readonly DEFAULT_TIMEZONE = 'America/Bogota';

  // Timezone dinámico - se actualiza post-login desde command.timezone (BD)
  private _organizationTimezone: string = this.DEFAULT_TIMEZONE;

  // 🔧 Configuración regional por defecto
  private readonly DEFAULT_LOCALE = 'es-CO';

  constructor(@Inject(LOCALE_ID) private locale: string) {
    // ✅ Configurar moment.js con la zona horaria por defecto
    moment.tz.setDefault(this._organizationTimezone);

    // ✅ CRÍTICO: Configurar locale español para nombres de meses consistentes
    // Esto asegura que los formatos 'DD MMM YYYY' siempre usen español
    // (ej: "10 mar 2025" en lugar de "10 Dec 2024")
    moment.locale('es');

    // ✅ CRÍTICO PARA EXCEL: Eliminar puntos de abreviaturas de meses
    // Por defecto, el locale 'es' usa "ene.", "feb.", "mar." con punto
    // Excel NO reconoce fechas con punto, necesita "ene", "feb", "mar"
    // Solución: Personalizar locale español sin puntos usando updateLocale()
    moment.updateLocale('es', {
      monthsShort: [
        'ene', 'feb', 'mar', 'abr', 'may', 'jun',
        'jul', 'ago', 'sep', 'oct', 'nov', 'dic'
      ]
    });
  }

  // =====================================================
  // 🏢 TIMEZONE ORGANIZACIONAL (Enterprise Pattern)
  // =====================================================

  /**
   * Actualiza el timezone operacional post-login.
   * Se llama desde verification.component.ts con command.timezone de la BD.
   * Patrón enterprise: Salesforce=Organization.TimeZoneSidKey
   */
  setOrganizationTimezone(timezone: string): void {
    if (timezone && timezone.includes('/')) {
      this._organizationTimezone = timezone;
      moment.tz.setDefault(timezone);
    }
  }

  /** Getter del timezone activo */
  getOrganizationTimezone(): string {
    return this._organizationTimezone;
  }

  /**
   * Extrae string "YYYY-MM-DD" de cualquier formato de fecha.
   * Seguro para columnas DATE de PostgreSQL que ahora llegan como string
   * gracias al boot script configure-pg-date-parser.js.
   */
  extractDateString(date: Date | string): string {
    if (!date) return '';
    // Fix: Date objects usan moment.tz (mismo patrón que formatDatabaseDate)
    // .toISOString() retorna UTC → después de 7PM Colombia da día+1
    const str = typeof date === 'string'
      ? date
      : moment.tz(date, this._organizationTimezone || this.DEFAULT_TIMEZONE).format('YYYY-MM-DDTHH:mm:ss');
    // Si ya es YYYY-MM-DD puro
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    // Extraer la parte de fecha de cualquier ISO string
    const match = str.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : '';
  }

  // =====================================================
  // 🛩️ MÉTODOS ESPECÍFICOS PARA MANTENIMIENTO AERONÁUTICO
  // =====================================================

  /**
   * 🛩️ MÉTODO CRÍTICO: Genera timestamp completo para logs de mantenimiento
   * Cumple con estándares FAA/ICAO para trazabilidad aeronáutica
   * Uso: Para logs de mantenimiento, horas de vuelo, reportes diarios
   */
  generateAviationTimestamp(date?: Date | string): AviationTimestamp {
    const inputDate = date ? moment.tz(date, this._organizationTimezone) : moment.tz(this._organizationTimezone);
    const utcMoment = inputDate.clone().utc();

    return {
      localDate: inputDate.format('YYYY-MM-DD'),
      localDateTime: inputDate.format('YYYY-MM-DD HH:mm:ss'),
      utcTimestamp: utcMoment.toISOString(),
      zuluTime: utcMoment.format('YYYY-MM-DDTHH:mm:ss[Z]'),
      unixTimestamp: utcMoment.unix()
    };
  }

  /**
   * 🛩️ MÉTODO CRÍTICO: Obtiene timestamp UTC para APIs aeronáuticas
   * Uso: Para enviar datos a sistemas de mantenimiento que requieren UTC
   */
  getUTCForAviationAPI(date?: Date | string): string {
    const timestamp = this.generateAviationTimestamp(date);
    return timestamp.utcTimestamp;
  }

  /**
   * 🛩️ MÉTODO CRÍTICO: Obtiene timestamp Zulu para logs de mantenimiento
   * Uso: Para logs técnicos que requieren formato Zulu estándar
   */
  getZuluTimeForMaintenanceLog(date?: Date | string): string {
    const timestamp = this.generateAviationTimestamp(date);
    return timestamp.zuluTime;
  }

  /**
   * 🛩️ MÉTODO CRÍTICO: Convierte timestamp UTC a local para mostrar en UI
   * Uso: Para mostrar fechas de APIs en formato local al usuario
   */
  convertUTCToLocalForDisplay(utcTimestamp: string): string {
    return moment.utc(utcTimestamp).tz(this.DEFAULT_TIMEZONE).format('YYYY-MM-DD HH:mm:ss');
  }

  /**
   * 🛩️ MÉTODO CRÍTICO: Valida timestamp para compliance aeronáutico
   * Uso: Para validar que los timestamps cumplan estándares FAA/ICAO
   */
  validateAviationTimestamp(timestamp: string): { isValid: boolean, errors: string[] } {
    const errors: string[] = [];

    if (!moment.utc(timestamp).isValid()) {
      errors.push('Timestamp UTC inválido');
    }

    const parsed = moment.utc(timestamp);
    const now = moment.utc();
    const diffHours = Math.abs(now.diff(parsed, 'hours'));

    if (diffHours > 8760) { // Más de 1 año
      errors.push('Timestamp fuera del rango válido (más de 1 año)');
    }

    if (parsed.isAfter(now)) {
      errors.push('Timestamp en el futuro no permitido');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * 🚨 MÉTODO DE MIGRACIÓN: Explica la confusión de nomenclatura del servicio anterior
   * Uso: Para documentar y ayudar en la migración desde mensajes.service.ts
   */
  explainMensajesServiceNamingConfusion(): void {
    console.group('🚨 CONFUSIÓN DE NOMENCLATURA EN mensajes.service.ts');
    console.warn('⚠️ IMPORTANTE: Los métodos del servicio anterior tenían nombres CONFUSOS:');
    console.log('');
    console.log('❌ date_with_UTC(fecha, format) - NAMING CONFUSO:');
    console.log('   - Nombre sugería que usaba UTC');
    console.log('   - En REALIDAD: NO usaba UTC, solo conversión local compleja');
    console.log('   - Equivalente correcto: formatLocalized()');
    console.log('');
    console.log('❌ date_without_UTC(fecha, format) - NAMING ERRÓNEO:');
    console.log('   - Nombre sugería que NO usaba UTC');
    console.log('   - En REALIDAD: SÍ usaba UTC con moment.utc()');
    console.log('   - Equivalente correcto: formatWithUTC()');
    console.log('');
    console.log('🔧 MIGRACIÓN CORRECTA:');
    console.log('   - Para fechas locales: usar formatLocalized()');
    console.log('   - Para fechas UTC: usar formatWithUTC()');
    console.log('   - Para timestamp aeronáutico: usar generateAviationTimestamp()');
    console.log('');
    console.log('✅ Este servicio tiene nombres CLAROS y comportamiento CONSISTENTE');
    console.groupEnd();
  }

  // =====================================================
  // 📅 MÉTODOS PRINCIPALES DE FECHAS (ACTUALIZADOS)
  // =====================================================

  /**
   * Obtiene la fecha actual según el timezone organizacional.
   * Post-login usa command.timezone de BD; pre-login usa fallback Colombia.
   */
  getCurrentDate(): Date {
    return moment.tz(this._organizationTimezone).toDate();
  }

  /**
   * Obtiene la fecha actual como string YYYY-MM-DD según timezone organizacional.
   */
  getCurrentDateString(): string {
    return moment.tz(this._organizationTimezone).format('YYYY-MM-DD');
  }

  /**
   * 🔥 MÉTODO PRINCIPAL: Crea un dateRange completo y seguro
   * Reemplaza todos los dateRange problemáticos del proyecto
   */
  createSafeDateRange(): DateRange {
    const now = moment.tz(this.DEFAULT_TIMEZONE);

    const startOfDay = now.clone().startOf('day');
    const endOfDay = now.clone().endOf('day');
    const firstDayOfMonth = now.clone().startOf('month');
    const lastDayOfMonth = now.clone().endOf('month');

    return {
      startDay: this.formatForInput(startOfDay.toDate()),
      endDay: this.formatForInput(endOfDay.toDate()),
      firstDayOfMonth: this.formatForInput(firstDayOfMonth.toDate()),
      lastDayOfMonth: this.formatForInput(lastDayOfMonth.toDate()),
      today: now.format('YYYY-MM-DD'),
      todayFormatted: now.format('YYYY-MM-DD') + 'T' + now.format('HH:mm:ss')
    };
  }

  /**
   * 🔥 MÉTODO PRINCIPAL: Inicializa dateSelectFilter de forma segura
   * Reemplaza las inicializaciones problemáticas en todos los componentes
   */
  initializeDateSelectFilter(sharedServiceDates?: string[]): string[] {
    const safeTodayFormatted = this.createSafeDateRange().todayFormatted;

    return sharedServiceDates && sharedServiceDates.length > 0
      ? sharedServiceDates
      : [safeTodayFormatted];
  }

  // =====================================================
  // 🌍 MÉTODOS DE CONVERSIÓN INTERNACIONAL (ACTUALIZADOS)
  // =====================================================

  /**
   * Convierte fecha local a UTC para envío a APIs
   * Uso: Para enviar fechas a backends que esperan UTC
   */
  toUTCForAPI(date: Date | string, timezone?: string): string {
    const tz = timezone || this.DEFAULT_TIMEZONE;
    return moment.tz(date, tz).utc().toISOString();
  }

  /**
   * 🔧 MÉTODO CRÍTICO: Convierte una fecha al ÚLTIMO MILISEGUNDO del día en UTC
   *
   * ⚠️ **PROPÓSITO**: Crear límites superiores para consultas de bases de datos
   * que filtran datos "hasta el final del día seleccionado" (operador SQL: <=)
   *
   * 📋 **CASOS DE USO**:
   * - Suma de horas de vuelo hasta el final del día
   * - Filtrado de mantenimientos completados hasta el día seleccionado
   * - Trayectorias GPS hasta el final del día
   * - Cualquier consulta que necesite "todos los datos HASTA el día X"
   *
   * 🔧 **FIX IMPLEMENTADO** (Oct 2025):
   * Problema anterior: Si eran las 8pm en Colombia (UTC-5), al convertir a UTC
   * ya pasaba al día siguiente (8pm COT = 1am UTC del día siguiente)
   * y luego establecía 23:59:59 de ESE día siguiente (incorrecto +1 día)
   *
   * Solución: Extraer año/mes/día de la fecha local, luego crear UTC para ese día específico
   *
   * @param date Fecha de entrada (cualquier formato: Date, string "YYYY-MM-DD", etc.)
   * @param timezone Zona horaria (opcional, por defecto 'America/Bogota')
   * @returns Fecha en formato ISO UTC con hora 23:59:59.999Z
   *
   * @example
   * // Entrada: "2025-10-01" a las 8:00 PM Colombia
   * // Salida: "2025-10-01T23:59:59.999Z" (día correcto ✅)
   *
   * // Uso en consulta SQL:
   * const endDate = this.dateService.getEndOfDayUTC("2025-10-01");
   * // SELECT * FROM records WHERE datereport <= '2025-10-01T23:59:59.999Z'
   */
  getEndOfDayUTC(date: Date | string, timezone?: string): string {
    const tz = timezone || this.DEFAULT_TIMEZONE;

    // Extraer año/mes/día de la fecha local (preserva el día original)
    const fechaMoment = moment.tz(date, tz);
    const year = fechaMoment.year();
    const month = fechaMoment.month(); // 0-indexed
    const day = fechaMoment.date();

    // Crear fecha UTC para el día específico a las 23:59:59.999
    return moment.utc([year, month, day, 23, 59, 59, 999]).toISOString();
  }

  /**
   * Convierte fecha UTC a local para mostrar al usuario
   * Uso: Para mostrar fechas que vienen de APIs en UTC
   */
  fromUTCToLocal(utcDate: string, timezone?: string): Date {
    const tz = timezone || this.DEFAULT_TIMEZONE;
    return moment.utc(utcDate).tz(tz).toDate();
  }

  /**
   * Formatea fecha según el locale del usuario
   * Uso: Para mostrar fechas en el formato regional correcto
   */
  formatLocalized(date: Date | string, format: DateFormat, timezone?: string): string {
    const tz = timezone || this.DEFAULT_TIMEZONE;
    const momentDate = moment.tz(date, tz);

    // Si es un formato predefinido de Angular, usar formatDate
    if (['short', 'medium', 'long', 'full', 'shortDate', 'mediumDate', 'longDate', 'fullDate', 'shortTime', 'mediumTime', 'longTime', 'fullTime'].includes(format)) {
      return formatDate(momentDate.toDate(), format, this.locale || this.DEFAULT_LOCALE);
    }

    // Si es un formato personalizado, usar moment
    return momentDate.format(format as string);
  }

  /**
   * Convierte fecha a la zona horaria especificada
   */
  convertToTimezone(date: Date | string, targetTimezone: string): Date {
    return moment.tz(date, this.DEFAULT_TIMEZONE).tz(targetTimezone).toDate();
  }

  // =====================================================
  // 🛠️ MÉTODOS DE UTILIDADES (ACTUALIZADOS)
  // =====================================================

  /**
   * Formatea fecha para inputs HTML (sin problemas de timezone)
   */
  formatForInput(date: Date | string): string {
    const momentDate = moment.tz(date, this.DEFAULT_TIMEZONE);
    return momentDate.format('YYYY-MM-DDTHH:mm:ss');
  }

  /**
   * Formatea solo la fecha (YYYY-MM-DD) sin tiempo
   */
  formatDateOnly(date: Date | string): string {
    return moment.tz(date, this.DEFAULT_TIMEZONE).format('YYYY-MM-DD');
  }

  /**
   * Formatea solo el tiempo (HH:mm:ss)
   */
  formatTimeOnly(date: Date | string): string {
    return moment.tz(date, this.DEFAULT_TIMEZONE).format('HH:mm:ss');
  }

  /**
   * Valida si una fecha es válida
   */
  isValidDate(date: any): boolean {
    return moment(date).isValid();
  }

  /**
   * Compara si dos fechas son el mismo día
   */
  isSameDay(date1: Date | string, date2: Date | string): boolean {
    return moment.tz(date1, this.DEFAULT_TIMEZONE).isSame(moment.tz(date2, this.DEFAULT_TIMEZONE), 'day');
  }

  /**
   * Verifica si una fecha es hoy
   */
  isToday(date: Date | string): boolean {
    return moment.tz(date, this.DEFAULT_TIMEZONE).isSame(moment.tz(this.DEFAULT_TIMEZONE), 'day');
  }

  /**
   * Verifica si una fecha es en el pasado
   */
  isPast(date: Date | string): boolean {
    return moment.tz(date, this.DEFAULT_TIMEZONE).isBefore(moment.tz(this.DEFAULT_TIMEZONE));
  }

  /**
   * Verifica si una fecha es en el futuro
   */
  isFuture(date: Date | string): boolean {
    return moment.tz(date, this.DEFAULT_TIMEZONE).isAfter(moment.tz(this.DEFAULT_TIMEZONE));
  }

  // =====================================================
  // 🛩️ MÉTODOS ESPECÍFICOS PARA TEMPLATES HTML
  // =====================================================

  /**
   * Formatea el día anterior a una fecha dada
   * Usado en templates para mostrar "Día Anterior"
   */
  formatPreviousDay(date: Date | string, format: string = 'DD MMM YYYY'): string {
    try {
      const targetDate = moment.tz(date, this.DEFAULT_TIMEZONE);
      const previousDay = targetDate.subtract(1, 'days');
      return previousDay.format(format);
    } catch (error) {
      console.warn('🛩️ Error al formatear día anterior:', error);
      return '';
    }
  }

  /**
   * Formatea fechas/horas para contexto aeronáutico
   * Reemplaza this._alert.date_with_UTC() en templates
   */
  formatAviationDateTime(date: Date | string, format: string = 'DD MMM YYYY HH:mm'): string {
    if (!date) return '';
    try {
      // Convertir la fecha usando moment.tz para consistencia de zona horaria
      const targetDate = moment.tz(date, this.DEFAULT_TIMEZONE);
      return targetDate.format(format);
    } catch (error) {
      console.warn('🛩️ Error al formatear fecha/hora aeronáutica:', error);
      return '';
    }
  }

  /**
   * Obtiene la hora local como momento
   * Reemplaza this._alert.dataTimeLocal() en componentes
   */
  dataTimeLocal(): any {
    // Usar moment.tz para obtener la hora local consistente
    return moment.tz(this.DEFAULT_TIMEZONE);
  }

  /**
   * 🔄 MIGRACIÓN: Reemplaza this._alert.date_without_UTC() - CORREGIDO PARA UTC-5
   * IMPORTANTE: Corregido para manejar fechas de Colombia (UTC-5) sin desfases
   * @param date Fecha a formatear
   * @param format Formato de salida (ej: 'YYYY-MM-DD', 'DD MMM YYYY')
   * @returns Fecha formateada preservando la fecha local
   */
  formatWithUTC(date: Date | string, format: string): string {
    // 🔧 CORRECCIÓN: Para fechas de Colombia (UTC-5), NO usar moment.utc() directo
    // porque causa desfase. En su lugar, usar moment.tz con la zona horaria correcta
    try {
      const momentDate = moment.tz(date, this.DEFAULT_TIMEZONE);
      return momentDate.format(format);
    } catch (error) {
      // 🔧 CORRECCIÓN ISS-FE-004: Usar métodos UTC en fallback para consistencia
      // Fallback a método local seguro
      const dateObj = date instanceof Date ? date : new Date(date);
      if (format === 'YYYY-MM-DD') {
        const year = dateObj.getUTCFullYear();
        const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getUTCDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      } else if (format === 'DD MMM YYYY') {
        const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
        const day = dateObj.getUTCDate().toString().padStart(2, '0');
        const month = monthNames[dateObj.getUTCMonth()];
        const year = dateObj.getUTCFullYear();
        return `${day} ${month} ${year}`;
      }
      return String(date);
    }
  }

  // Normalizar UTC Local para las fechas que ya vienen en UTC (Todas las que tengan Z al final)
  normalizeUTCToLocal(date: string | Date): Date {
    const d = new Date(date);

    // 🔥 Convierte UTC → Local
    return new Date(
      d.getUTCFullYear(),
      d.getUTCMonth(),
      d.getUTCDate(),
      d.getUTCHours(),
      d.getUTCMinutes(),
      d.getUTCSeconds(),
      d.getUTCMilliseconds()
    );
  }


  /**
   * Formatea fecha para comparaciones (alias de formatWithUTC corregido)
   * Reemplaza this._alert.date_without_UTC() en comparaciones
   */
  formatForComparison(date: Date | string, format: string): string {
    return this.formatWithUTC(date, format);
  }

  /**
   * 🔧 MÉTODO ESPECÍFICO: Formatea fechas que vienen de la base de datos (UTC-5)
   * Garantiza manejo correcto de fechas de Colombia sin desfases
   * Uso: Para fechas datereport y similares que vienen del backend
   */
  /**
   * 🔧 MÉTODO CRÍTICO: Formatea fechas que vienen de la base de datos
   *
   * 📅 PROBLEMA DE FECHAS UTC DOCUMENTADO (Sep 2025):
   * ================================================
   * Cuando el backend envía strings de fecha "YYYY-MM-DD" (sin hora):
   * - Backend: Usa _getLocalDateKey() que extrae con getUTCFullYear/Month/Date
   * - Envía: "2025-09-30" (string puro, sin información de timezone)
   * - Frontend problema: moment.utc("2025-09-30") + moment.tz() causaba desfase
   *
   * CAUSA DEL DESFASE (UTC-5 Colombia):
   * - "2025-09-30" → moment.utc() → 2025-09-30T00:00:00.000Z (medianoche UTC)
   * - Al formatear con timezone local: 2025-09-29T19:00:00-05:00 (Colombia)
   * - Resultado: Muestra "29 Sep" en lugar de "30 Sep" ❌
   *
   * SOLUCIÓN IMPLEMENTADA:
   * - Detectar strings YYYY-MM-DD con regex
   * - Parsear componentes (año, mes, día) manualmente
   * - Crear moment UTC con componentes explícitos
   * - Formatear manteniendo UTC (sin conversión a timezone local)
   * - Resultado: "30 Sep 2025" ✅
   *
   * @param dateFromDB - Fecha de BD (string "YYYY-MM-DD" o Date object)
   * @param format - Formato de salida ('DD MMM YYYY', 'YYYY-MM-DD', etc)
   * @returns Fecha formateada correctamente
   */
  formatDatabaseDate(dateFromDB: Date | string, format: string): string {
    if (!dateFromDB) return '';

    try {
      let momentDate;

      // PASO 1: Extraer string YYYY-MM-DD de CUALQUIER formato de entrada
      // Patrón "Date as String": Business Dates NUNCA pasan por moment.tz()
      let dateStr: string | null = null;

      if (typeof dateFromDB === 'string') {
        const match = dateFromDB.match(/^(\d{4}-\d{2}-\d{2})/);
        dateStr = match ? match[1] : null;
      } else if (dateFromDB instanceof Date) {
        // Usar moment.tz para extraer fecha según timezone organizacional
        // getUTCDate() falla después de 7PM Colombia (midnight UTC = día siguiente)
        dateStr = moment.tz(dateFromDB, this._organizationTimezone || this.DEFAULT_TIMEZONE).format('YYYY-MM-DD');
      }

      if (dateStr) {
        // PASO 2: Formatear con moment.utc() - NUNCA moment.tz() para Business Dates
        const [year, month, day] = dateStr.split('-').map(Number);
        momentDate = moment.utc()
          .year(year)
          .month(month - 1)  // month() es 0-indexed
          .date(day)
          .startOf('day');   // 00:00:00.000 UTC
      } else {
        // Fallback para formatos no reconocidos (timestamps con hora, etc.)
        momentDate = moment.tz(dateFromDB, this.DEFAULT_TIMEZONE);
      }

      // Formatear según el tipo solicitado
      switch (format) {
        case 'YYYY-MM-DD':
          return momentDate.format('YYYY-MM-DD');
        case 'DD MMM YYYY':
          return momentDate.format('DD MMM YYYY');
        case 'DD MMMM YYYY':
          return momentDate.format('DD MMMM YYYY');
        default:
          return momentDate.format(format);
      }
    } catch (error) {
      // 🛡️ FALLBACK SEGURO: También usa métodos UTC para evitar desfase
      let dateObj;
      if (typeof dateFromDB === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateFromDB)) {
        // Para strings YYYY-MM-DD, agregar hora UTC explícita
        dateObj = new Date(dateFromDB + 'T00:00:00Z');
      } else {
        dateObj = dateFromDB instanceof Date ? dateFromDB : new Date(dateFromDB);
      }

      // Usar métodos getUTC*() para extraer componentes sin conversión
      if (format === 'YYYY-MM-DD') {
        const year = dateObj.getUTCFullYear();
        const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getUTCDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      } else if (format === 'DD MMM YYYY') {
        const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
        const day = dateObj.getUTCDate().toString().padStart(2, '0');
        const month = monthNames[dateObj.getUTCMonth()];
        const year = dateObj.getUTCFullYear();
        return `${day} ${month} ${year}`;
      }
      return String(dateFromDB);
    }
  }

  /**
   * Obtiene el primer día del mes actual
   */
  getFirstDayOfMonth(): Date {
    return moment.tz(this.DEFAULT_TIMEZONE).startOf('month').toDate();
  }

  /**
   * Obtiene el último día del mes actual
   */
  getLastDayOfMonth(): Date {
    return moment.tz(this.DEFAULT_TIMEZONE).endOf('month').toDate();
  }

  /**
   * Obtiene el primer día del año actual
   */
  getFirstDayOfYear(): Date {
    return moment.tz(this.DEFAULT_TIMEZONE).startOf('year').toDate();
  }

  /**
   * Obtiene el último día del año actual
   */
  getLastDayOfYear(): Date {
    return moment.tz(this.DEFAULT_TIMEZONE).endOf('year').toDate();
  }

  /**
   * Crea un rango de fechas
   */
  createDateRange(startDate: Date | string, endDate: Date | string): { start: string, end: string } {
    const start = moment.tz(startDate, this.DEFAULT_TIMEZONE);
    const end = moment.tz(endDate, this.DEFAULT_TIMEZONE);

    return {
      start: start.format('YYYY-MM-DD'),
      end: end.format('YYYY-MM-DD')
    };
  }

  // =====================================================
  // 🌐 MÉTODOS DE ZONA HORARIA
  // =====================================================

  /**
   * Obtiene información de la zona horaria actual
   */
  getCurrentTimezoneInfo(): TimeZoneInfo {
    const now = moment.tz(this.DEFAULT_TIMEZONE);
    return {
      name: this.DEFAULT_TIMEZONE,
      offset: now.format('Z'),
      abbreviation: now.format('z')
    };
  }

  /**
   * Obtiene las zonas horarias soportadas
   */
  getSupportedTimezones(): { [key: string]: string } {
    return this.SUPPORTED_TIMEZONES;
  }

  /**
   * Detecta la zona horaria del usuario
   */
  detectUserTimezone(): string {
    const userTimezone = moment.tz.guess();
    return this.SUPPORTED_TIMEZONES[userTimezone] ? userTimezone : this.DEFAULT_TIMEZONE;
  }

  // =====================================================
  // 🔧 MÉTODOS DE COMPATIBILIDAD Y MIGRACIÓN
  // =====================================================

  /**
   * Reemplaza el patrón problemático: new Date().toISOString().split("T")[0]
   */
  getProblematicPatternReplacement(): string {
    return this.getCurrentDateString();
  }

  /**
   * Reemplaza el patrón problemático: new Date().toISOString().split("T")[0]
   */
  getTodayStringReplacement(): string {
    return this.getCurrentDateString();
  }

  /**
   * Reemplaza el patrón problemático: new Date().setHours(0, 0, 0, 0)
   */
  getTodayMomentReplacement(): string {
    return moment.tz(this.DEFAULT_TIMEZONE).startOf('day').toISOString();
  }

  /**
   * 🔄 MIGRACIÓN: Convierte timestamp a formato de fecha
   * @param value Timestamp en segundos o milisegundos
   * @returns Fecha formateada en formato 'DD MMM YYYY'
   */
  intToDate(value: number): string {
    try {
      // Si el valor parece estar en milisegundos, lo usamos directamente
      if (value > 1000000000000) {  // Valor mayor que 1 billón es milisegundos
        return moment.tz(value, this.DEFAULT_TIMEZONE).format('DD MMM YYYY');
      } else {
        return moment.tz(value * 1000, this.DEFAULT_TIMEZONE).format('DD MMM YYYY'); // Si es en segundos, lo convertimos a milisegundos
      }
    } catch (error) {
      console.warn('🛩️ Error al convertir timestamp a fecha:', error, 'Value:', value);
      return '';
    }
  }

  /**
   * 🔄 MIGRACIÓN: Compara fecha con la fecha actual
   * @param dateTime Fecha a comparar
   * @returns -1 si dateTime es anterior, 0 si es igual, 1 si es posterior
   */
  compareDateTime(dateTime: any): number {
    const currentDate = this.getCurrentDateForComparison();
    const dateToCompare = this.formatForComparison(dateTime, 'YYYY-MM-DD');

    return this.compareDate(currentDate, dateToCompare);
  }

  /**
   * 🔄 MIGRACIÓN: Compara dos fechas en formato string
   * @param dateTimeA Primera fecha
   * @param dateTimeB Segunda fecha
   * @returns -1 si A < B, 0 si A = B, 1 si A > B
   */
  compareDate(dateTimeA: string, dateTimeB: string): number {
    if (dateTimeA > dateTimeB) {
      return 1; // A es mayor
    } else if (dateTimeA < dateTimeB) {
      return -1; // B es mayor
    }
    return 0; // Son iguales
  }

  /**
   * 🔄 MIGRACIÓN: Obtiene fecha actual para comparaciones
   * @returns Fecha actual en formato YYYY-MM-DD
   */
  private getCurrentDateForComparison(): string {
    // Convertir moment a Date antes de pasar a formatForComparison
    return this.formatForComparison(moment.tz(this.DEFAULT_TIMEZONE).toDate(), 'YYYY-MM-DD');
  }

  // =====================================================
  // 🧪 MÉTODOS DE DEBUGGING Y VALIDACIÓN
  // =====================================================

  /**
   * Debug de comparación de fechas para detectar problemas
   */
  debugDateComparison(): void {
    console.group('🛩️ Debug de Fechas Aeronáuticas');
    const now = moment.tz(this.DEFAULT_TIMEZONE);
    const aviationTimestamp = this.generateAviationTimestamp(now.toDate());
    console.log('📍 Fecha Local (Colombia):', aviationTimestamp.localDateTime);
    console.log('🌍 Timestamp UTC:', aviationTimestamp.utcTimestamp);
    console.log('🛩️ Zulu Time:', aviationTimestamp.zuluTime);
    console.log('⏰ Unix Timestamp:', aviationTimestamp.unixTimestamp);
    // Comparar con métodos problemáticos
    const problematicPattern = moment.tz(this.DEFAULT_TIMEZONE).toDate().toISOString().split('T')[0];
    const safePattern = this.getCurrentDateString();
    console.log('❌ Patrón Problemático:', problematicPattern);
    console.log('✅ Patrón Seguro:', safePattern);
    console.log('🔍 ¿Son iguales?:', problematicPattern === safePattern);
    console.groupEnd();
  }

  /**
   * 🚨 MÉTODO DE DEBUGGING: Verifica migración de métodos confusos de mensajes.service.ts
   * Uso: Para validar que las migraciones desde métodos confusos sean correctas
   */
  debugMensajesServiceMigration(): void {
    const testDate = new Date();

    console.group('🚨 DEBUG: Migración desde mensajes.service.ts');
    console.log('Fecha de prueba:', testDate);
    console.log('');

    // Simular el comportamiento histórico de los métodos confusos
    const simulatedDateWithUTC = this.formatLocalized(testDate, 'YYYY-MM-DD');
    const simulatedDateWithoutUTC = this.formatWithUTC(testDate, 'YYYY-MM-DD HH:mm:ss');

    console.log('🔄 MIGRACIÓN DE MÉTODOS CONFUSOS:');
    console.log('');
    console.log('❌ date_with_UTC (CONFUSO) → ✅ formatLocalized():');
    console.log('   Resultado:', simulatedDateWithUTC);
    console.log('   Uso: Para fechas locales en UI');
    console.log('');
    console.log('❌ date_without_UTC (ERRÓNEO) → ✅ formatWithUTC():');
    console.log('   Resultado:', simulatedDateWithoutUTC);
    console.log('   Uso: Para fechas UTC en logs/APIs');
    console.log('');

    console.log('✅ MEJOR OPCIÓN - generateAviationTimestamp():');
    const aviationTimestamp = this.generateAviationTimestamp(testDate);
    console.log('   localDate (para UI):', aviationTimestamp.localDate);
    console.log('   utcTimestamp (para API):', aviationTimestamp.utcTimestamp);
    console.log('   zuluTime (para logs):', aviationTimestamp.zuluTime);
    console.log('');

    console.log('⚠️ VERIFICACIÓN: Los nombres ahora son CLAROS y CONSISTENTES');
    console.groupEnd();
  }

  /**
   * Valida un objeto que contiene fechas
   */
  validateDateObject(obj: any): { isValid: boolean, errors: string[] } {
    const errors: string[] = [];

    const checkDate = (value: any, path: string) => {
      if (value && typeof value === 'string' && value.includes('T')) {
        if (!this.isValidDate(value)) {
          errors.push(`Fecha inválida en ${path}: ${value}`);
        }
      }
    };

    const traverse = (object: any, currentPath: string = '') => {
      for (const key in object) {
        const value = object[key];
        const path = currentPath ? `${currentPath}.${key}` : key;

        if (value && typeof value === 'object' && !Array.isArray(value)) {
          traverse(value, path);
        } else {
          checkDate(value, path);
        }
      }
    };

    traverse(obj);

    return { isValid: errors.length === 0, errors };
  }
} 